/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Carousel';
import { page as SamplePage } from '../../help/sample_pages/CarouselPage';

const name = 'Carousel';

const CarouselManager: ComponentManagerFn<'Carousel'> = (
  platform: Platform,
): ComponentManager<'Carousel'> => {
  return {
    ...baseComponentManager<'Carousel'>(platform, 'Carousel'),
    defaultValue,
    defaultChildren: ['FlexPanel', 'FlexPanel', 'FlexPanel'],
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          'FlexPanel',
          'Card',
          'DataStoreReference',
          'HorizontalSplitPanel',
          'TabPanel',
          'VerticalSplitPanel',
        ] as ItemType[]
      ).includes(childType),
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'interval':
        case 'stopOnHover':
          return itemData.autoPlay;
        default:
          return true;
      }
    },
    props: {
      autoPlay: {
        label: 'Enable Auto-Play',
        type: 'boolean',
        helpText: 'Automatically transition slides at the specified interval.',
      },
      interval: {
        label: 'Auto-Play Interval (ms)',
        type: 'numberInput',
        helpText:
          'Set the duration (in milliseconds) between slide transitions when auto-play is enabled.',
      },
      stopOnHover: {
        label: 'Pause Auto-Play on Hover',
        type: 'boolean',
        helpText: 'Pause auto-play when the user hovers over the carousel.',
      },
      infiniteLoop: {
        label: 'Loop Slides Continuously',
        type: 'boolean',
        helpText:
          'Enable seamless looping so the carousel restarts after the last slide.',
      },
      showArrows: {
        label: 'Show Navigation Arrows',
        type: 'boolean',
        helpText: 'Enable this to display previous/next navigation arrows.',
      },
      showStatus: {
        label: 'Show Slide Status',
        type: 'boolean',
        helpText:
          "Display the current slide number and total slide count (e.g., '1 of 5').",
      },
      showIndicators: {
        label: 'Show Slide Indicators',
        type: 'boolean',
        helpText: 'Enable this to display navigation dots below the carousel.',
      },
      useKeyboardArrows: {
        label: 'Enable Keyboard Navigation',
        type: 'boolean',
        helpText:
          'Allow users to navigate slides using the left and right arrow keys.',
      },
      emulateTouch: {
        label: 'Enable Touch Dragging',
        type: 'boolean',
        helpText:
          'Allow users to change slides by dragging on touch devices and with a mouse.',
      },
      selectedItem: {
        label: 'Default Slide Index',
        type: 'numberInput',
        helpText:
          'Set the index (starting from 0) of the slide to display by default.',
      },
      transitionTime: {
        label: 'Slide Transition Duration (ms)',
        type: 'numberInput',
        helpText:
          'Set the duration (in milliseconds) of the slide transition animation.',
      },
      indicatorColor: {
        label: 'Slide indicator Color',
        type: 'ColorExpression',
        helpText: 'Set the color of the Slide Indicators.',
      },
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'sizeAware',
    icon: { iconName: 'rectangle-vertical-history' },
    description: `**Overview**<br/>
Imagine you have three critical dashboards to show your team, or five promotional banners for your customers, but you only have one screen to work with. Instead of forcing users to scroll endlessly or jump between different pages, you can bring the content to them.
The Carousel acts as a rotating stage. It allows you to stack multiple components (like Flex Panels or Cards) into a single container and transition between them smoothly. It keeps your interface clean while delivering high-density information.

**1. Setting the Stage**<br/>
Before you configure the behavior, you need content. Think of the Carousel as a container waiting for slides. By right-clicking the component in your tree and adding children (like Flex Panels), you are creating the individual scenes that your users will see.

**2. Bringing it to Life (Playback)**<br/>
Once your content is in place, you need to decide how the story unfolds. Should the content rotate automatically, or wait for the user?
* **Automate the Flow:** If you are building a passive display (like a TV dashboard or a hero banner), switch on Enable Auto-Play.
* **Set the Pace:** With auto-play on, use the Auto-Play Interval to define the rhythm. A short interval (e.g., 3000ms) adds energy, while a longer one gives users time to read detailed charts.
* **Respect the User:** If a user spots something interesting, they’ll want to look closer. Enable Pause Auto-Play on Hover so the rotation stops politely when their mouse enters the area, and resumes when they leave.
* **The Infinite Loop:** To keep the experience seamless, enable Loop Slides Continuously. This ensures that after the last slide, the carousel elegantly sweeps back to the first one without a hard stop.

**3. Navigation**<br/>
Even with auto-play, users often want to take control. You can provide visual cues to help them navigate.
* **Direct Control:** Enable Show Navigation Arrows to give users standard "Previous" and "Next" buttons on the sides.
* **Context at a Glance:** For a more subtle approach, toggle Show Slide Indicators. These dots at the bottom show users exactly how many slides exist and where they are in the sequence. You can even match these to your brand using the Slide Indicator Color.
* **The Status Check:** If you are displaying a sequence (like "Step 1 of 4"), turn on Show Slide Status to display a clear text counter.

**4. Interaction & Polish**<br/>
The difference between a "good" UI and a "great" one is often in how it feels to use.
* **Touch & Swipe:** In a mobile-first world, users expect to swipe. Enable Enable Touch Dragging to let them drag slides with their finger or mouse—it makes the UI feel tactile and responsive.
* **Keyboard Power:** For power users moving quickly through a dashboard, Enable Keyboard Navigation lets them flip through slides using just their arrow keys.
* **Smooth Transitions:** Finally, adjust the Slide Transition Duration. A lower number makes the switch snappy and energetic; a higher number makes it glide smoothly.`,
    helpRenders: [
      {
        title: 'Carousel',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: [
          'autoPlay',
          'interval',
          'stopOnHover',
          'infiniteLoop',
          'showArrows',
          'showStatus',
          'showIndicators',
          'useKeyboardArrows',
          'emulateTouch',
          'selectedItem',
          'transitionTime',
          'indicatorColor',
        ],
        item: {
          itemId: 'carousel',
          props: defaultValue,
          children: ['flexPanel2', 'flexPanel3'],
          type: 'Carousel',
          parent: 'root',
        },
      },
    ],
  };
};

export default CarouselManager;
